// @rpg-unit/front-end-flow: the front end (tier 4; composition framework, decision 0077). Cut from spike 002's
// `src/ui/screens.ts` (title, loading, pause, game over) and `src/ui/index.ts` (the flow's screens) on 2026-10-20. On
// @rpg/ui's screen stack it gives a game:
//
//   - `frontEndScreens(o)`: the title (Continue when there is a save to continue, Start, then the title entries other
//     features add), the loading screen, the pause menu (Resume, the pause entries other features add, in their order,
//     then Quit to title) and game over (back to the title);
//   - `frontEndFlow(ui, o)`: the screens a game flow drives (one call per state: title, loading, play, pause, game
//     over), where a newer state always wins over a title still waiting for its data (a save list).
//
// The seams: what the game's flow does (start, resume, quit) arrives as each screen's params; Continue asks a port
// (`continueFrom`: can it, and do it); the other features' menu items arrive as registered entries (`addPauseEntry`,
// `addTitleEntry`: a label, an action name, an order, and what a press does: push their screen, usually). It imports
// no other feature: the save slots, settings and controls screens are pushed by id.
import { Button, h, Menu, Screen, type ScreenContext, type ScreenRender, type Ui } from "@rpg/ui";

/** This unit's version (the manifest's and package.json's). */
export const FRONT_END_FLOW_VERSION = "0.1.0" as const;

/** The screen ids this feature registers. */
export const FRONT_END_SCREENS = { title: "title", loading: "loading", pause: "pause", gameover: "gameover" } as const;

/** The title's params: the flow's start (the first gesture: call it inside the press). */
export interface TitleParams {
  start(): void;
}
/** The pause menu's params: the flow's ways out. */
export interface PauseParams {
  resume(): void;
  toTitle(): void;
}
/** Game over's params. */
export interface GameOverParams {
  toTitle(): void;
}

/** A menu item another feature (or the game) adds to the title or the pause menu. */
export interface MenuEntry<P> {
  /** The label's string id. */
  label: string;
  /** The button's `data-action` (what checks and specs press). */
  action: string;
  /** Lower first; entries with the same order keep the order they were added in (default 0). */
  order?: number;
  /** Shown only while this says so (a hidden entry keeps its place, so focus doesn't jump when it appears). */
  show?: () => boolean;
  /** What a press does, with the screen's params (e.g. push a screen that resumes play when it's done). */
  press(ctx: ScreenContext, params: P): void;
}

/** String ids for the screens' texts (the defaults are spike 002's ids). */
export interface FrontEndLabels {
  gameTitle: string;
  continue: string;
  start: string;
  loading: string;
  paused: string;
  resume: string;
  quit: string;
  gameOver: string;
  toTitle: string;
}

/** Spike 002's string ids, the defaults. */
export const DEFAULT_FRONT_END_LABELS: FrontEndLabels = {
  gameTitle: "game.title",
  continue: "ui.continue",
  start: "ui.startGame",
  loading: "ui.loading",
  paused: "ui.paused",
  resume: "ui.resume",
  quit: "ui.quitToTitle",
  gameOver: "ui.gameOver",
  toTitle: "ui.toTitle",
};

/** What the screens need. */
export interface FrontEndOptions {
  labels?: Partial<FrontEndLabels>;
  /** Continue on the title: shown (first, focused) while `can()`; a press runs `go()` and starts the game when it
   *  resolves true (a save loaded). Omit for a game with no saves. */
  continueFrom?: { can(): boolean; go(): Promise<boolean> };
}

/** Planted defects, so the checks can prove they fail: `ignoreOrder` lists entries as added, whatever their order;
 *  `staleTitle` lets a title that waited for its data replace a newer state (play, pause). */
export const FRONT_END_FAULTS = { ignoreOrder: false, staleTitle: false };

/** Entries sorted by order, stable. */
function ordered<P>(entries: readonly MenuEntry<P>[]): MenuEntry<P>[] {
  if (FRONT_END_FAULTS.ignoreOrder) return [...entries];
  return entries.map((e, i) => ({ e, i })).sort((a, b) => (a.e.order ?? 0) - (b.e.order ?? 0) || a.i - b.i).map((x) => x.e);
}

/** The title, loading, pause and game-over screens, and the registration seams for their menus. */
export function frontEndScreens(o: FrontEndOptions = {}): {
  screens: Record<string, ScreenRender>;
  /** Add an item to the pause menu (between Resume and Quit); returns the remover. */
  addPauseEntry(e: MenuEntry<PauseParams>): () => void;
  /** Add an item to the title (after Start); returns the remover. */
  addTitleEntry(e: MenuEntry<TitleParams>): () => void;
  /** The entries now, in the order shown (for checks). */
  entries(): { title: string[]; pause: string[] };
} {
  const l: FrontEndLabels = { ...DEFAULT_FRONT_END_LABELS, ...o.labels };
  const pause: MenuEntry<PauseParams>[] = [];
  const title: MenuEntry<TitleParams>[] = [];
  const adder =
    <P>(list: MenuEntry<P>[]) =>
    (e: MenuEntry<P>): (() => void) => {
      list.push(e);
      return () => {
        const i = list.indexOf(e);
        if (i >= 0) list.splice(i, 1);
      };
    };
  const item = <P>(ctx: ScreenContext, p: P, e: MenuEntry<P>) => (e.show && !e.show() ? null : h(Button, { label: ctx.t(e.label), action: e.action, onPress: () => e.press(ctx, p) }));

  const screens: Record<string, ScreenRender> = {
    [FRONT_END_SCREENS.title]: (ctx) => {
      const p = ctx.params as TitleParams;
      const can = o.continueFrom?.can() === true;
      return h(
        Screen,
        { ctx, title: ctx.t(l.gameTitle), variant: "splash" },
        h(
          Menu,
          null,
          can
            ? h(Button, {
                label: ctx.t(l.continue),
                variant: "primary",
                autofocus: true,
                action: "continue",
                onPress: () => void o.continueFrom!.go().then((ok) => ok && p.start()),
              })
            : null,
          h(Button, { label: ctx.t(l.start), variant: can ? "plain" : "primary", autofocus: !can, action: "start", onPress: () => p.start() }),
          ...ordered(title).map((e) => item(ctx, p, e)),
        ),
      );
    },
    [FRONT_END_SCREENS.loading]: (ctx) => h(Screen, { ctx, title: ctx.t(l.loading), variant: "splash" }),
    [FRONT_END_SCREENS.pause]: (ctx) => {
      const p = ctx.params as PauseParams;
      return h(
        Screen,
        { ctx, title: ctx.t(l.paused), dim: true, onBack: () => p.resume(), showBack: false },
        h(
          Menu,
          null,
          h(Button, { label: ctx.t(l.resume), variant: "primary", autofocus: true, action: "resume", onPress: () => p.resume() }),
          ...ordered(pause).map((e) => item(ctx, p, e)),
          h(Button, { label: ctx.t(l.quit), action: "quit", onPress: () => p.toTitle() }),
        ),
      );
    },
    [FRONT_END_SCREENS.gameover]: (ctx) => {
      const p = ctx.params as GameOverParams;
      return h(Screen, { ctx, title: ctx.t(l.gameOver), dim: true }, h(Menu, null, h(Button, { label: ctx.t(l.toTitle), variant: "primary", autofocus: true, action: "to-title", onPress: () => p.toTitle() })));
    },
  };
  return {
    screens,
    addPauseEntry: adder(pause),
    addTitleEntry: adder(title),
    entries: () => ({ title: ordered(title).map((e) => e.action), pause: ordered(pause).map((e) => e.action) }),
  };
}

/** The screens a game flow drives, one call per state it enters; each replaces what the UI shows. */
export interface FrontEndFlowScreens {
  /** The title (after `beforeTitle` settles). `start` is the first gesture: call it inside the press's event. */
  title(a: TitleParams): void;
  loading(): void;
  /** Play: no screen. */
  play(o?: unknown): void;
  pause(a: PauseParams): void;
  gameover(a: GameOverParams): void;
}

/**
 * The flow's screens over a UI stack. The title first waits for `beforeTitle` (e.g. reading the save list, so Continue
 * shows at once); a state entered while it waits wins (the title then never appears over it).
 */
export function frontEndFlow(ui: Pick<Ui, "reset" | "clear">, o: { beforeTitle?: () => Promise<unknown> } = {}): FrontEndFlowScreens {
  let token = 0;
  return {
    title: (a) => {
      const mine = ++token;
      void (o.beforeTitle?.() ?? Promise.resolve()).finally(() => (mine === token || FRONT_END_FAULTS.staleTitle) && ui.reset(FRONT_END_SCREENS.title, a));
    },
    loading: () => void (++token, ui.reset(FRONT_END_SCREENS.loading)),
    play: () => void (++token, ui.clear()),
    pause: (a) => void (++token, ui.reset(FRONT_END_SCREENS.pause, a)),
    gameover: (a) => void (++token, ui.reset(FRONT_END_SCREENS.gameover, a)),
  };
}
